"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, MapPin, Phone } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useRef } from "react";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | null)[]>([]);
  useEffect(() => {
    inputRefs.current.forEach(input => {
      if (input) {
        const handleFocus = () => {
          input.style.transition = "border-color 120ms ease-in-out";
        };
        input.addEventListener("focus", handleFocus);
        return () => input.removeEventListener("focus", handleFocus);
      }
    });
  }, []);
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary/20 rounded-lg shadow-soft animate-fade-in-up">
        <p className="text-foreground text-lg font-medium">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 hover:scale-[1.02] transition-transform duration-[80ms] ease-in-out">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      },
      maxLength: {
        value: 50,
        message: "Name must not exceed 50 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Your name" {...field} ref={el => {
          field.ref(el);
          inputRefs.current[0] = el;
        }} className="transition-[border-color] duration-[120ms] focus-visible:border-primary focus-visible:ring-primary/30" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} ref={el => {
          field.ref(el);
          inputRefs.current[1] = el;
        }} className="transition-[border-color] duration-[120ms] focus-visible:border-primary focus-visible:ring-primary/30" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required",
      pattern: {
        value: /^[0-9\s\-\+\(\)]+$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 123-4567" {...field} ref={el => {
          field.ref(el);
          inputRefs.current[2] = el;
        }} className="transition-[border-color] duration-[120ms] focus-visible:border-primary focus-visible:ring-primary/30" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about your restaurant and how we can help..." {...field} ref={el => {
          field.ref(el);
          inputRefs.current[3] = el;
        }} rows={5} className="w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs transition-[border-color] duration-[120ms] outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in-up">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full hover:scale-[1.02] transition-transform duration-[80ms] ease-in-out">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section ref={ref} className={`py-24 px-6 bg-background transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            Get in Touch
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Ready to grow your restaurant's email list? Contact us today for a consultation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className={`bg-muted-light-background rounded-lg p-8 shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          boxShadow: "inset 0 0 0 1px hsla(28, 95%, 50%, 0.15)",
          transitionDelay: "100ms"
        }}>
            <FormProvider formId="contact-form-get-in-touch" sectionName="get-in-touch" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you! We'll get back to you within 24 hours.">
              <ContactFormContent />
            </FormProvider>
          </div>

          <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: "200ms"
        }}>
            <div className="bg-muted rounded-lg border border-border overflow-hidden shadow-soft" style={{
            height: "400px",
            position: "relative"
          }}>
              <div className="absolute inset-0 bg-secondary/10 flex items-center justify-center transition-transform duration-[var(--duration-slow)] ease-linear" style={{
              transform: isVisible ? "translateY(0)" : "translateY(-10px)"
            }}>
                <div className="text-center p-8">
                  <MapPin className="size-16 text-secondary mx-auto mb-4" />
                  <p className="text-muted-foreground text-sm">Map View</p>
                </div>
              </div>
            </div>

            <div className="mt-8 space-y-6 animate-fade-in-up-delay-200">
              <div className="flex items-start gap-4">
                <MapPin className="size-6 text-primary shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Address</h3>
                  <p className="text-muted-foreground">123 Main St, Springfield</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone className="size-6 text-primary shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                  <p className="text-muted-foreground">(555) 123-4567</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}